import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, ShieldCheck } from "lucide-react";
import { Badge } from "@/modules/shared/ui/components/badge";
import { Button } from "@/modules/shared/ui/components/button";
import { Skeleton } from "@/modules/shared/ui/components/skeleton";
import { cn } from "@/lib/utils";
import { IncreaseWorkflowStep } from "../../enums/increase-workflow-step";
import { REPORTING_LINE_ISSUE_LABELS } from "../../enums/reporting-line-issue-type";
import {
  useIncreasePeriods,
  useOpenPeriodToManagers,
  useReportingLineIssues,
} from "../../hooks/useReportingLines";

const SALARY_INCREASES_ROUTE = "/salary-increases";
const ALLOCATION_ROUTE = "/salary-increases/allocate";
const SKELETON_ROWS = 4;

function pluralise(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-ZA", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function EmptyState({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
      <p className="text-sm font-medium">{title}</p>
      <div className="mt-1 text-xs text-muted-foreground">{children}</div>
    </div>
  );
}

export default function ReportingLineCleanupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPeriodId = searchParams.get("periodId");

  const periods = useIncreasePeriods();
  const period = requestedPeriodId
    ? periods.data?.find((p) => p.id === requestedPeriodId)
    : periods.data?.find(
        (p) => p.currentStep === IncreaseWorkflowStep.CleanReportingLines,
      );
  const atCleanupStep =
    period?.currentStep === IncreaseWorkflowStep.CleanReportingLines;

  // Reporting lines are read from Sage only for a period that is at this step.
  const issues = useReportingLineIssues(atCleanupStep ? period?.id : undefined);
  const openToManagers = useOpenPeriodToManagers();

  const issueCount = issues.data?.issueCount ?? 0;
  const allClear = !!issues.data && issueCount === 0;
  const checking = issues.isFetching;

  const handleOpen = () => {
    if (!period) return;
    openToManagers.mutate(period.id, {
      onSuccess: (result) => {
        if (result.unlinkedManagerCount > 0) {
          toast.warning(
            `${pluralise(result.unlinkedManagerCount, "manager")} not linked to a ProTalent employee, so their teams have no pool. Run the Sage 300 employee import to link them.`,
          );
        }
        navigate(ALLOCATION_ROUTE);
      },
    });
  };

  const header = (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Step 2 · HR Operations
      </p>
      <h1 className="text-2xl font-semibold">Reporting Line Cleanup</h1>
      <p className="text-sm text-muted-foreground mt-1">
        Every employee needs a valid, active manager in Sage 300 before the
        period can open for allocation.
      </p>
    </div>
  );

  if (periods.isLoading) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
        {header}
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    );
  }

  if (periods.isError) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
        {header}
        <EmptyState title="Could not load increase periods">
          Please refresh the page and try again.
        </EmptyState>
      </div>
    );
  }

  if (!period) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
        {header}
        <EmptyState
          title={
            requestedPeriodId
              ? "That increase period was not found"
              : "No period is waiting on reporting line cleanup"
          }
        >
          <Link to={SALARY_INCREASES_ROUTE} className="underline">
            Back to Salary Increases
          </Link>
        </EmptyState>
      </div>
    );
  }

  if (!atCleanupStep) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
        {header}
        <EmptyState title={`${period.name} is not at the reporting line step`}>
          {period.currentStep === IncreaseWorkflowStep.ManagerAllocation ? (
            <>
              It has already been opened to managers.{" "}
              <Link to={ALLOCATION_ROUTE} className="underline">
                Go to allocations
              </Link>
            </>
          ) : (
            <Link to={SALARY_INCREASES_ROUTE} className="underline">
              Back to Salary Increases
            </Link>
          )}
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
      {header}

      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4",
          allClear
            ? "border-emerald-500/40 bg-emerald-500/5"
            : "border-amber-500/40 bg-amber-500/5",
        )}
      >
        <div className="flex items-start gap-3">
          {allClear ? (
            <ShieldCheck className="mt-0.5 size-5 text-emerald-600" />
          ) : (
            <AlertTriangle className="mt-0.5 size-5 text-amber-600" />
          )}
          <div>
            <p className="text-sm font-medium">
              {issues.isLoading
                ? "Checking Sage 300…"
                : allClear
                  ? "All reporting lines are valid"
                  : `${pluralise(issueCount, "reporting line issue")} to fix in Sage 300`}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {allClear
                ? "The gate is open. Managers can now be given their pools."
                : "Fix these in Sage 300, then refresh. The period cannot advance until Sage shows none."}
              {issues.data && ` Last checked ${formatTime(issues.data.checkedAt)}.`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={checking}
            onClick={() => issues.refetch()}
          >
            <RefreshCw className={cn("mr-1.5 size-3.5", checking && "animate-spin")} />
            {checking ? "Checking…" : "Refresh from Sage"}
          </Button>
          <Button
            size="sm"
            disabled={!allClear || checking || openToManagers.isPending}
            onClick={handleOpen}
          >
            {openToManagers.isPending ? "Opening…" : "Open period to managers"}
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
        <table className="w-full min-w-[44rem] text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium">Employee</th>
              <th className="px-4 py-2.5 text-left font-medium">Business unit</th>
              <th className="px-4 py-2.5 text-left font-medium">Issue</th>
              <th className="px-4 py-2.5 text-left font-medium">Current manager</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {issues.isLoading &&
              Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <tr key={index}>
                  <td colSpan={4} className="px-4 py-3">
                    <Skeleton className="h-6 w-full" />
                  </td>
                </tr>
              ))}

            {issues.isError && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-xs text-destructive">
                  Could not read reporting lines from Sage 300. Try refreshing.
                </td>
              </tr>
            )}

            {issues.isSuccess && issues.data.issues.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  No reporting line issues were found in Sage 300.
                </td>
              </tr>
            )}

            {issues.data?.issues.map((issue) => (
              <tr key={issue.sageEmployeeId}>
                <td className="px-4 py-3">
                  <p className="font-medium">{issue.employeeName}</p>
                  <p className="text-xs text-muted-foreground">{issue.employeeNumber}</p>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{issue.businessUnit}</td>
                <td className="px-4 py-3">
                  <Badge
                    variant="outline"
                    className="border-amber-500/30 bg-amber-500/15 text-[10px] text-amber-700 dark:text-amber-400"
                  >
                    {REPORTING_LINE_ISSUE_LABELS[issue.issueType]}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {issue.currentManagerName ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
